import { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Loader2, X, Upload,
  GripVertical, Tags, Image as ImageIcon, Package
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

const iconOptions = [
  { value: 'flame', label: 'Flame' },
  { value: 'shirt', label: 'Shirt' },
  { value: 'circle-dot', label: 'Circle Dot' },
  { value: 'lamp', label: 'Lamp' },
  { value: 'book-open', label: 'Book' },
  { value: 'landmark', label: 'Temple' },
  { value: 'gift', label: 'Gift' },
  { value: 'star', label: 'Star' },
  { value: 'heart', label: 'Heart' },
];

export default function AdminShopCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_hindi: '',
    description: '',
    icon: 'flame',
    order: 0,
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [uploadType, setUploadType] = useState('icon');

  useEffect(() => {
    fetchCategories();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/categories`, getAuthHeaders());
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      name_hindi: '',
      description: '',
      icon: 'flame',
      order: categories.length,
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      name_hindi: category.name_hindi || '',
      description: category.description || '',
      icon: category.icon || 'flame',
      order: category.order || 0,
      is_active: category.is_active !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Category name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, getAuthHeaders());
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, formData, getAuthHeaders());
        toast.success('Category created successfully');
      }
      
      setShowModal(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    if (category.product_count > 0) {
      toast.error(`Cannot delete category with ${category.product_count} products. Move or delete products first.`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/categories/${category.id}`, getAuthHeaders());
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleActive = async (category) => {
    try {
      await axios.put(`${API}/categories/${category.id}`, 
        { is_active: !category.is_active }, 
        getAuthHeaders()
      );
      toast.success(category.is_active ? 'Category hidden' : 'Category visible');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file || !editingCategory) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/categories/${editingCategory.id}/image?image_type=${type}`,
        formData,
        { 
          ...getAuthHeaders(),
          headers: { 
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data' 
          }
        }
      );
      
      toast.success(`${type === 'icon' ? 'Icon' : 'Banner'} uploaded`);
      fetchCategories();
      
      // Update editing category
      const updated = categories.find(c => c.id === editingCategory.id);
      if (updated) {
        setEditingCategory({
          ...editingCategory,
          [type === 'icon' ? 'image' : 'banner']: response.data.image_url
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleReorder = async (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    
    // Swap
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    // Update order values
    const orderUpdates = newCategories.map((cat, idx) => ({
      id: cat.id,
      order: idx
    }));
    
    try {
      await axios.put(`${API}/categories/reorder`, orderUpdates, getAuthHeaders());
      setCategories(newCategories.map((cat, idx) => ({ ...cat, order: idx })));
      toast.success('Order updated');
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Categories</h1>
          <p className="text-slate-500 text-sm mt-1">Manage product categories</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          data-testid="add-category-btn"
        >
          <Plus className="w-5 h-5" />
          Add Category
        </button>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12">
            <Tags className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No categories found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`flex items-center gap-4 p-4 hover:bg-slate-50 ${
                  !category.is_active ? 'opacity-60' : ''
                }`}
              >
                {/* Drag Handle & Order */}
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="flex flex-col">
                    <button
                      onClick={() => handleReorder(index, 'up')}
                      disabled={index === 0}
                      className="p-0.5 hover:text-slate-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18,15 12,9 6,15"></polyline>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleReorder(index, 'down')}
                      disabled={index === categories.length - 1}
                      className="p-0.5 hover:text-slate-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6,9 12,15 18,9"></polyline>
                      </svg>
                    </button>
                  </div>
                  <span className="text-xs w-6 text-center">{index + 1}</span>
                </div>

                {/* Category Image */}
                <div className="w-14 h-14 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0">
                  {category.image ? (
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-slate-300" />
                    </div>
                  )}
                </div>

                {/* Category Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                    {!category.is_active && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">Hidden</span>
                    )}
                  </div>
                  {category.name_hindi && (
                    <p className="text-sm text-amber-600">{category.name_hindi}</p>
                  )}
                  <p className="text-sm text-slate-500 truncate">{category.description || 'No description'}</p>
                </div>

                {/* Product Count */}
                <div className="text-center">
                  <div className="flex items-center gap-1 text-slate-500">
                    <Package className="w-4 h-4" />
                    <span className="text-sm font-medium">{category.product_count || 0}</span>
                  </div>
                  <span className="text-xs text-slate-400">Products</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleActive(category)}
                    className={`p-2 rounded-lg transition-colors ${
                      category.is_active ? 'hover:bg-slate-100 text-slate-400' : 'bg-red-100 text-red-600'
                    }`}
                    title={category.is_active ? 'Hide category' : 'Show category'}
                  >
                    {category.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(category)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    disabled={category.product_count > 0}
                    className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={category.product_count > 0 ? 'Cannot delete - has products' : 'Delete'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Hindi Name</label>
                <input
                  type="text"
                  value={formData.name_hindi}
                  onChange={(e) => setFormData({...formData, name_hindi: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 text-amber-500 rounded focus:ring-amber-200"
                  />
                  <span className="text-sm text-slate-700">Active (Visible in shop)</span>
                </label>
              </div>

              {/* Images - Only for editing */}
              {editingCategory && (
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium text-slate-700">Category Images</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Icon Image */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-2">Icon Image</label>
                      <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative">
                        {editingCategory.image ? (
                          <img src={editingCategory.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                        >
                          {uploadingImage ? (
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                          ) : (
                            <Upload className="w-6 h-6 text-white" />
                          )}
                        </button>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => handleImageUpload(e, 'icon')}
                        className="hidden"
                      />
                    </div>

                    {/* Banner Image */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-2">Banner Image</label>
                      <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative">
                        {editingCategory.banner ? (
                          <img src={editingCategory.banner} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => bannerInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                        >
                          {uploadingImage ? (
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                          ) : (
                            <Upload className="w-6 h-6 text-white" />
                          )}
                        </button>
                      </div>
                      <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => handleImageUpload(e, 'banner')}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
